import React, { useState, useRef } from 'react';
import { Card, Btn, Badge, Modal, FormRow, SectionTitle, EmptyState, StatCard } from '../components/UI';
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';

const CATEGORIES = {
  trees: { label: 'Trees', desc: 'Fruit trees, nut trees, ornamental trees', color: 'green' },
  fruitbushes: { label: 'Fruit bushes & canes', desc: 'Soft fruit, berries, raspberries, currants', color: 'soil' },
  infrastructure: { label: 'Infrastructure', desc: 'Raised beds, fencing, greenhouse, structures', color: 'soil' },
  irrigation: { label: 'Irrigation', desc: 'Irrigation systems, water butts, piping, fittings', color: 'stone' },
  tools: { label: 'Tools & equipment', desc: 'Hand tools, machinery, hardware', color: 'stone' },
  soil: { label: 'Soil & compost', desc: 'Compost, manure, mulch, soil improvers', color: 'soil' },
  seeds: { label: 'Seeds & plants', desc: 'Seeds, plugs, bare root, bulbs', color: 'green' },
  other: { label: 'Other', desc: 'Anything else', color: 'stone' },
};

function categoryColor(cat) {
  return CATEGORIES[cat]?.color || 'stone';
}

function formatDate(d) {
  try { return format(parseISO(d), 'd MMM yyyy'); } catch { return d || ''; }
}

function CostModal({ onSave, onClose, prefill, title }) {
  const [form, setForm] = useState({
    description: prefill?.description || '',
    amount: prefill?.amount || '',
    category: prefill?.category || 'seeds',
    date: prefill?.date || format(new Date(), 'yyyy-MM-dd'),
    supplier: prefill?.supplier || '',
    notes: prefill?.notes || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleSave() {
    if (!form.description.trim() || !form.amount) return;
    onSave({ ...form, amount: parseFloat(form.amount) });
  }

  return (
    <Modal title={title || 'Log expense'} onClose={onClose}>
      <FormRow label="Description">
        <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. 60L compost bag" autoFocus />
      </FormRow>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormRow label="Amount (£)">
          <input type="number" step="0.01" min="0" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
        </FormRow>
        <FormRow label="Date">
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        </FormRow>
      </div>
      <FormRow label="Category">
        <select value={form.category} onChange={e => set('category', e.target.value)}>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </FormRow>
      <FormRow label="Supplier">
        <input value={form.supplier} onChange={e => set('supplier', e.target.value)} placeholder="e.g. Delfland Nurseries" />
      </FormRow>
      <FormRow label="Notes">
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes…" />
      </FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={!form.description.trim() || !form.amount}>Save</Btn>
      </div>
    </Modal>
  );
}

function InvoiceUploadModal({ onParsed, onClose }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');

  async function handleParse() {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const mediaType = file.type || 'image/jpeg';
      const isImage = mediaType.startsWith('image/');
      const contentBlock = isImage
        ? { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } }
        : { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } };
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{ role: 'user', content: [contentBlock, { type: 'text', text: `Extract the key details from this receipt or invoice for a kitchen garden. Return ONLY valid JSON (no markdown, no backticks) in this exact format: {"description":"short description of main item(s)","amount":12.99,"date":"2024-05-28","supplier":"supplier name or empty string","category":"one of: trees,fruitbushes,infrastructure,irrigation,tools,soil,seeds,other","notes":"any useful detail"}. If you cannot determine a field use empty string or 0 for amount.` }] }]
        })
      });
      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const clean = text.replace(/```json|```/g, '').trim();
      onParsed(JSON.parse(clean));
    } catch (e) {
      setError('Could not parse the invoice. Please fill in the details manually.');
      onParsed({});
    }
    setParsing(false);
  }

  return (
    <Modal title="Upload invoice or receipt" onClose={onClose} width={420}>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
        Upload a photo or scan of a receipt and Claude will extract the details automatically.
      </p>
      <FormRow label="Receipt / invoice">
        <input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files[0])} style={{ padding: '6px 8px', cursor: 'pointer' }} />
      </FormRow>
      {error && <div style={{ fontSize: 12, color: 'var(--red-600)', marginBottom: '0.75rem' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleParse} disabled={!file || parsing}>{parsing ? 'Reading invoice…' : 'Extract details'}</Btn>
      </div>
    </Modal>
  );
}

function SpreadsheetUploadModal({ onImport, onClose }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  async function handleParse() {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });

      // Read as text for CSV, or use Claude for Excel
      const isCSV = file.name.endsWith('.csv');
      let textContent = '';

      if (isCSV) {
        textContent = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = ev => res(ev.target.result);
          r.onerror = rej;
          r.readAsText(file);
        });
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4000,
          messages: [{
            role: 'user',
            content: isCSV
              ? `Parse this CSV of garden costs and return ONLY a JSON array (no markdown, no backticks). Each item should have: description (string), amount (number, pounds), date (string YYYY-MM-DD or empty), supplier (string or empty), category (one of: trees, fruitbushes, infrastructure, irrigation, tools, soil, seeds, other — choose the best fit), notes (string or empty). Here is the CSV:\n\n${textContent}`
              : [
                  { type: 'document', source: { type: 'base64', media_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', data: base64 } },
                  { type: 'text', text: `Parse this spreadsheet of garden costs and return ONLY a JSON array (no markdown, no backticks). Each item should have: description (string), amount (number, pounds), date (string YYYY-MM-DD or empty), supplier (string or empty), category (one of: trees, fruitbushes, infrastructure, irrigation, tools, soil, seeds, other — choose the best fit), notes (string or empty).` }
                ]
          }]
        })
      });

      const data = await response.json();
      const text = data.content?.map(c => c.text || '').join('') || '';
      const clean = text.replace(/```json|```/g, '').trim();
      const rows = JSON.parse(clean);
      if (!Array.isArray(rows)) throw new Error('Not an array');
      setPreview(rows);
    } catch (e) {
      setError('Could not parse the spreadsheet. Make sure it has columns for description, amount and date.');
    }
    setParsing(false);
  }

  function handleImport() {
    if (!preview) return;
    onImport(preview);
  }

  return (
    <Modal title="Import spreadsheet" onClose={onClose} width={600}>
      {!preview ? (
        <>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Upload a CSV or Excel (.xlsx) spreadsheet of costs. Claude will read the columns and map them into the tracker automatically.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Your spreadsheet should have columns for description, amount and ideally date and supplier. Column names don't need to be exact — Claude will figure it out.
          </p>
          <FormRow label="Spreadsheet file">
            <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files[0])} style={{ padding: '6px 8px', cursor: 'pointer' }} />
          </FormRow>
          {error && <div style={{ fontSize: 12, color: 'var(--red-600)', marginBottom: '0.75rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={handleParse} disabled={!file || parsing}>{parsing ? 'Reading spreadsheet…' : 'Read file'}</Btn>
          </div>
        </>
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--moss-600)', marginBottom: '1rem' }}>
            ✓ Found {preview.length} expense{preview.length !== 1 ? 's' : ''}. Review below then click Import to add them all.
          </p>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', marginBottom: '1rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', position: 'sticky', top: 0 }}>
                  {['Description', 'Amount', 'Date', 'Category', 'Supplier'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px', color: 'var(--text-primary)' }}>{row.description}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 500 }}>£{parseFloat(row.amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-secondary)' }}>{row.date || '—'}</td>
                    <td style={{ padding: '7px 10px' }}><Badge color={categoryColor(row.category)}>{CATEGORIES[row.category]?.label || row.category}</Badge></td>
                    <td style={{ padding: '7px 10px', color: 'var(--text-secondary)' }}>{row.supplier || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn onClick={() => setPreview(null)}>← Back</Btn>
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={handleImport}>Import {preview.length} expenses</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

export default function Costs({ state, dispatch }) {
  const [addModal, setAddModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [spreadsheetModal, setSpreadsheetModal] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkCat, setBulkCat] = useState('trees');
  const { costs = [] } = state;

  const filtered = costs
    .filter(c => filterCat === 'all' || c.category === filterCat)
    .filter(c => !search || c.description?.toLowerCase().includes(search.toLowerCase()) || c.supplier?.toLowerCase().includes(search.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date));

  const total = costs.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const now = new Date();
  const monthTotal = costs.filter(c => {
    try { return isWithinInterval(parseISO(c.date), { start: startOfMonth(now), end: endOfMonth(now) }); }
    catch { return false; }
  }).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  const byCategory = Object.keys(CATEGORIES).map(k => ({
    key: k, label: CATEGORIES[k].label,
    total: costs.filter(c => c.category === k).reduce((s, c) => s + (parseFloat(c.amount) || 0), 0),
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  const maxCat = byCategory[0]?.total || 1;

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(selected.size === sorted.length ? new Set() : new Set(sorted.map(c => c.id)));
  }

  function applyBulkCategory() {
    selected.forEach(id => {
      const cost = costs.find(c => c.id === id);
      if (cost) dispatch({ type: 'UPDATE_COST', cost: { ...cost, category: bulkCat } });
    });
    setSelected(new Set());
  }

  function bulkDelete() {
    if (!window.confirm(`Delete ${selected.size} expense${selected.size !== 1 ? 's' : ''}?`)) return;
    selected.forEach(id => dispatch({ type: 'DELETE_COST', id }));
    setSelected(new Set());
  }

  function handleSpreadsheetImport(rows) {
    rows.forEach(row => {
      dispatch({ type: 'ADD_COST', cost: {
        id: 'cost_' + Date.now() + Math.random(),
        description: row.description || 'Imported expense',
        amount: parseFloat(row.amount) || 0,
        category: CATEGORIES[row.category] ? row.category : 'other',
        date: row.date || format(new Date(), 'yyyy-MM-dd'),
        supplier: row.supplier || '',
        notes: row.notes || '',
      }});
    });
    setSpreadsheetModal(false);
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 960 }} className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>Costs</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Infrastructure & growing expenses</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn onClick={() => setSpreadsheetModal(true)}>↑ Import spreadsheet</Btn>
          <Btn onClick={() => setInvoiceModal(true)}>↑ Upload invoice</Btn>
          <Btn variant="primary" onClick={() => { setPrefill(null); setAddModal(true); }}>+ Add expense</Btn>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: '1.5rem' }}>
        <StatCard label="Total spent" value={`£${total.toFixed(0)}`} />
        <StatCard label="This month" value={`£${monthTotal.toFixed(0)}`} />
        <StatCard label="Expenses logged" value={costs.length} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '1rem', marginBottom: '1.5rem' }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search expenses…"
              style={{ width: 180, fontSize: 13, marginRight: 4 }} />
            {['all', ...Object.keys(CATEGORIES)].map(k => (
              <button key={k} onClick={() => setFilterCat(k)} style={{
                fontSize: 12, padding: '3px 10px', borderRadius: 20, border: '1px solid', cursor: 'pointer',
                borderColor: filterCat === k ? 'var(--accent)' : 'var(--border-md)',
                background: filterCat === k ? 'var(--accent-light)' : 'transparent',
                color: filterCat === k ? 'var(--accent)' : 'var(--text-secondary)',
              }}>{k === 'all' ? 'All' : CATEGORIES[k].label}</button>
            ))}
          </div>

          {selected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: 'var(--accent-light)', borderRadius: 'var(--radius-md)',
              marginBottom: '0.75rem', flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>{selected.size} selected</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Move to:</span>
              <select value={bulkCat} onChange={e => setBulkCat(e.target.value)} style={{ fontSize: 13, width: 'auto' }}>
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <Btn size="sm" variant="primary" onClick={applyBulkCategory}>Apply</Btn>
              <Btn size="sm" variant="danger" onClick={bulkDelete}>Delete selected</Btn>
              <Btn size="sm" onClick={() => setSelected(new Set())}>Clear</Btn>
            </div>
          )}

          {sorted.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 8, borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
              <input type="checkbox"
                checked={selected.size === sorted.length && sorted.length > 0}
                onChange={toggleSelectAll}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }} />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {selected.size === sorted.length ? 'Deselect all' : `Select all ${sorted.length}`}
              </span>
            </div>
          )}

          {sorted.length === 0 ? (
            <EmptyState icon="₤" title="No expenses yet" body="Log your first expense, upload a receipt, or import a spreadsheet."
              action={<Btn variant="primary" onClick={() => setAddModal(true)}>Add expense</Btn>} />
          ) : sorted.map(c => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: '1px solid var(--border)',
              background: selected.has(c.id) ? 'var(--accent-light)' : 'transparent',
              borderRadius: selected.has(c.id) ? 'var(--radius-sm)' : 0,
              paddingLeft: selected.has(c.id) ? 6 : 0,
              transition: 'all 0.1s',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)', marginTop: 3, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{c.description}</span>
                    <Badge color={categoryColor(c.category)}>{CATEGORIES[c.category]?.label || c.category}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {c.date ? formatDate(c.date) : ''}
                    {c.supplier ? ` · ${c.supplier}` : ''}
                  </div>
                  {c.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{c.notes}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 500, fontFamily: 'var(--font-display)' }}>£{parseFloat(c.amount).toFixed(2)}</span>
                <button onClick={() => setEditingCost(c)}
                  style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: '1px solid var(--border-md)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: '2px 8px' }}>✎</button>
                <button onClick={() => { if (window.confirm('Delete this expense?')) dispatch({ type: 'DELETE_COST', id: c.id }); }}
                  style={{ fontSize: 14, color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>✕</button>
              </div>
            </div>
          ))}
        </Card>

        <div>
          <Card style={{ marginBottom: '1rem' }}>
            <SectionTitle>By category</SectionTitle>
            {byCategory.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>No data yet.</p>
            ) : byCategory.map(c => (
              <div key={c.key} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => setFilterCat(c.key)}>{c.label}</span>
                  <span style={{ fontWeight: 500 }}>£{c.total.toFixed(0)}</span>
                </div>
                <div style={{ height: 5, background: 'var(--bg-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round((c.total / maxCat) * 100)}%`, background: 'var(--accent)', borderRadius: 3 }} />
                </div>
              </div>
            ))}
          </Card>

          <Card>
            <SectionTitle>Categories</SectionTitle>
            {Object.entries(CATEGORIES).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <Badge color={v.color}>{v.label}</Badge>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, flex: 1 }}>{v.desc}</div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {addModal && (
        <CostModal prefill={prefill} title="Add expense"
          onSave={cost => { dispatch({ type: 'ADD_COST', cost: { id: 'cost_' + Date.now(), ...cost } }); setAddModal(false); setPrefill(null); }}
          onClose={() => { setAddModal(false); setPrefill(null); }} />
      )}

      {editingCost && (
        <CostModal prefill={editingCost} title="Edit expense"
          onSave={updated => { dispatch({ type: 'UPDATE_COST', cost: { ...editingCost, ...updated } }); setEditingCost(null); }}
          onClose={() => setEditingCost(null)} />
      )}

      {invoiceModal && (
        <InvoiceUploadModal
          onParsed={data => { setInvoiceModal(false); setPrefill(data); setAddModal(true); }}
          onClose={() => setInvoiceModal(false)} />
      )}

      {spreadsheetModal && (
        <SpreadsheetUploadModal
          onImport={handleSpreadsheetImport}
          onClose={() => setSpreadsheetModal(false)} />
      )}
    </div>
  );
}
